import {Injectable} from "@angular/core";
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, of, Subject} from 'rxjs';
import {stubModelDetail, stubDD, stubAddSecMasterRes, stubSecurityCustCategory} from './test-double';
import {delay} from 'rxjs/operators';
import {IrbAppEvents} from "@irb/app.events";
import {IAlertDialogData} from "@irb/app.model";
import {UtilityService} from "@irb-common/services/utility.service";
import {AppService} from "@irb/app.service";
import {map} from "rxjs/operators";

@Injectable({
    providedIn: "root"
})
export class EditorMmcModelService {
    private dropDownItems: any;
    dropDownsDataLoaded = new Subject<any>();
    importTargets = new Subject<any>();
    dataSaved = new Subject<any>();
    sundryAgreePass = new Subject<any>();
    paidAgreePass = new Subject<any>();
    mmcServiceUrlPrefix = '/mmc';
    private enableMmcApis = false;

    private httpOptions: any = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
        })
    };

    constructor(private _http: HttpClient, private util: UtilityService, public as: AppService) {
        this.enableMmcApis = this.as.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_MMC_APIS || false;
        if (this.enableMmcApis)
            this.mmcServiceUrlPrefix = '/model-market-service';
    }

    getDropDownItems() {
        return this.dropDownItems;
    }

    clearDropDownItems() {
        this.dropDownItems = null;
    }

    getMMCDropDowns() {
        let url = `/model-market-service/get-mmc-drop-downs`; ///mmc/get-mmc-drop-downs
        this._http.get(url, this.httpOptions).subscribe((ddItems: any) => {
            ddItems = this.defineResponse(ddItems)
            this.dropDownItems = ddItems;
            this.dropDownsDataLoaded.next(null);
        });
    }

    getModelDetail(modelMarketId: number) {
        const url: string = '/mmc/model/' + modelMarketId;
        return this._http.get(url, this.httpOptions);
    }

    addSecurityFromMaster(params: Array<string>) {
        return this._http.post("/mmc/security-master-validation", JSON.stringify(params), this.httpOptions);
    }

    getSecurityCustCategorySettings() {
        return this._http.get("/mmc/get-security-cust-category-settings", this.httpOptions);
    }

    saveUpdateModel(modelData: any) {
        if (modelData.modelMarketId) {
            return this._http.put('/mmc/model', JSON.stringify(modelData), this.httpOptions);
        } else {
            return this._http.post('/mmc/model', JSON.stringify(modelData), this.httpOptions);
        }
    }

    resubmitModel(modelMarketId: number) {
        return this._http.get('/mmc/model/' + modelMarketId + '/resubmit', this.httpOptions);
    }

    addNewTargets(newSecs: any) {
        this.importTargets.next(newSecs);
    }

    importSecurities(formData: any): Observable<Object> {
        return this._http.post('/mmc/import-securities', formData, {responseType: "text" as "json"});
    }
    getModelMarketHistory(modelMarketId: number) {
        const url = `/model-market-service/model/${modelMarketId}/history`;
        return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
    }

    triggerDataSaved(newData: any) {
        this.dataSaved.next(newData);
    }
    getMarketingDocuments(modelMarketId: number, advisorView: boolean = false) {
        ///model/{modelId}/marketing-document-details/{advisorView}
        const url = `/model-market-service/model/${modelMarketId}/marketing-document-details/${advisorView}`;
        return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
    }
    uploadMarketingDocument(modelMarketId: number,formData: any): Observable<Object> { // not in use
        const url: string = '/mmc/model/' + modelMarketId + '/marketing-document';
        return this._http.post(url, formData, {responseType: "text" as "json"});
    }
    // addMarketingDocumentLink(modelMarketId: number, linkData: any): Observable<Object> {
    //     const url: string = '/mmc/link-marketing-document/'+modelMarketId;
    //     return this._http.post(url, JSON.stringify(linkData),this.httpOptions);
    // }
    getMarketingDocumentPDF(id: number) {
        const url: string = '/mmc/view-marketing-document/'+id;
        return this._http.get(url,{responseType: 'arraybuffer'});
    }
    saveMarketingDocuments(modelMarketId: number, marketingDocumentsData: any) {
        const url: string = '/mmc/model/' + modelMarketId + '/marketing-document-details/';
        return this._http.post(url, JSON.stringify(marketingDocumentsData),this.httpOptions);
    }

    unPublishModel(params: any) {
        //{"modelMarketId":106}
        return this._http.post('/mmc/unpublish-model', JSON.stringify(params), this.httpOptions);
    }

    publishModel(params: any) {
        //{"modelMarketId":106}
        return this._http.post('/mmc/publish-model', JSON.stringify(params), this.httpOptions);
    }

    getAdvisorModelDetail(idx: number, sortField?: string, filter?: string) {
        // /mmc/get-next-previous-model-shop-detail?Filter=modelMarketName%3Dtest&sort(+isSubscribed)&index=10
        let url = '/mmc/get-next-previous-model-shop-detail?';
        if (filter) {
            filter = filter.replace('modelMarketName%3D', 'modelMarketName=');
            url += 'Filter=' + encodeURIComponent(filter) + '&';
        }
        if (sortField) {
            url += 'sort(' + sortField + ')&'
        }
        url += 'index=' + idx;
        return this._http.get(url, this.httpOptions);
    }
    getAdvisorModelDetailByModelMarketId(modelMarketId: any) {
        const url = `/model-market-service/get-model-shop-detail/${modelMarketId}`;
        return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
    }

    getAdvisorNote(modelMarketId: number) {
        const url = `/model-market-service/get-note/${modelMarketId}`;
        return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
    }

    saveAdvisorNote(params: any) {
        const url = `/model-market-service/set-note`;
        return this._http.post(url, JSON.stringify(params), this.httpOptions).pipe(map(res => this.defineResponse(res)));
    }

    subscribeToModel(modelMarketId: number) {
        const url = '/mmc/subscribe-model/' + modelMarketId;
        return this._http.get(url, this.httpOptions);
    }

    checkSundryAuthorization() {
        const url = `/model-market-service/check-sundry-authorization`;
        return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
    }

    setSundryAccount(params: any) {
        return this._http.post('/mmc/set-sundry-account', JSON.stringify(params), this.httpOptions);
    }

    sundryPassGoNext(sundryAcct: any) {
        this.sundryAgreePass.next(sundryAcct);
    }

    paidPassGoNext() {
        this.paidAgreePass.next(null);
    }
    getInformaSAMLResponse(sourceId: string) {
        return this._http.get("/mmc/wealth-iq/saml-response-info?psnId=" + sourceId, this.httpOptions);
    }

    deleteUnPublish(modelMarketId: number) {
        let arr: any = [];
        arr.push(modelMarketId);
        return this._http.post('/mmc/delete-models', JSON.stringify(arr), this.httpOptions);
    }

    getMMCFirms() {
        return this._http.get('/irebalone/env/environments', this.httpOptions);
    }

    getMasterNumbers(envId: any) {
        return this._http.get('/irebalone/env/' + envId + '/master-accounts', this.httpOptions);
    }

    showUpdateManFeeConfirm(originalData: any, sundryAcct: any, passNext = true) {
        let d= this.util.getEasternTime(new Date().getTime());
        d = this.util.formatDateTimeString(new Date(d), "MM/dd/yyyy hh:mm tt") + ' ET';
        const msg = "<div class='block'>You have updated your Management Fee Account Number.</div>" +
          "<div class='block'>Date and Time: " + d + "</div>" +
          "<div class='block'>Previous: " + originalData.sundryAcct + "</div>" +
          "<div class='block'>Current Management Fee Account: " + sundryAcct + "</div>";
        const dProceedBtnLabel = passNext ? 'Continue' : 'Close';
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dTitle: 'Management Fee Account Number Update',
            dProceedBtnLabel: dProceedBtnLabel,
            altMsgHTML: msg,
            onProceed: () => {
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'success',
                    summary: 'Management Fee Account Authorization',
                    detail: 'Management Fee Account Number has been updated.'
                });
                if (passNext)
                    this.sundryPassGoNext(sundryAcct);
            }
        } as IAlertDialogData);
    }

    defineResponse(res: any) {
        return  res && res.data && res.data.irebalResponse ? res.data.irebalResponse : res;
    }
}

